import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { 
  Plus,
  Calendar,
  Edit,
  Trash2,
  AlertCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { format } from 'date-fns';
import ConfirmDialog from '@/components/ConfirmDialog';
import useConfirmDialog from '@/hooks/useConfirmDialog';

export default function EnrollmentPeriods() {
  const [periods, setPeriods] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [selectedEmployerId, setSelectedEmployerId] = useState('');
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [formData, setFormData] = useState({
    employer_id: '',
    period_type: 'open_enrollment',
    name: '',
    start_date: '',
    end_date: '',
    status: 'upcoming',
    description: '',
    allow_enrollment_outside_window: false,
    enforce_60_day_wait: true
  });
  const { confirmDialogProps, openConfirm } = useConfirmDialog();

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    setLoading(true);
    try {
      const user = await api.auth.me();
      const systemAdmin = user?.user_role === 'admin' || user?.role === 'ADMIN';
      setIsSystemAdmin(systemAdmin);

      const employersData = await api.entities.Employer.list();
      setEmployers(employersData);

      if (systemAdmin) {
        const defaultEmployerId = employersData?.[0]?.id || '';
        setSelectedEmployerId(defaultEmployerId);
        if (defaultEmployerId) {
          const periodsData = await api.entities.EnrollmentPeriod.filter({ employer_id: defaultEmployerId });
          setPeriods(periodsData);
        } else {
          setPeriods([]);
        }
      } else {
        const periodsData = await api.entities.EnrollmentPeriod.list('-created_at');
        setPeriods(periodsData);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployerChange = async (value) => {
    setSelectedEmployerId(value);
    setLoading(true);
    try {
      const periodsData = await api.entities.EnrollmentPeriod.filter({ employer_id: value });
      setPeriods(periodsData);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load enrollment periods');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (period = null) => {
    if (period) {
      setEditingPeriod(period);
      setFormData({
        employer_id: period.employer_id || '',
        period_type: period.period_type || 'open_enrollment',
        name: period.name || '',
        start_date: period.start_date || '',
        end_date: period.end_date || '',
        status: period.status || 'upcoming',
        description: period.description || '',
        allow_enrollment_outside_window: period.allow_enrollment_outside_window || false,
        enforce_60_day_wait: period.enforce_60_day_wait !== false
      });
    } else {
      setEditingPeriod(null);
      setFormData({
        employer_id: isSystemAdmin ? (selectedEmployerId || '') : (employers?.[0]?.id || ''),
        period_type: 'open_enrollment',
        name: '',
        start_date: '',
        end_date: '',
        status: 'upcoming',
        description: '',
        allow_enrollment_outside_window: false,
        enforce_60_day_wait: true
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.employer_id || !formData.name || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingPeriod) {
        await api.entities.EnrollmentPeriod.update(editingPeriod.id, formData);
        toast.success('Enrollment period updated');
      } else {
        await api.entities.EnrollmentPeriod.create(formData);
        toast.success('Enrollment period created');
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save enrollment period');
    }
  };

  const deletePeriod = async (period) => {
    try {
      await api.entities.EnrollmentPeriod.delete(period.id);
      toast.success('Enrollment period deleted');
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete enrollment period');
    }
  };

  const confirmDelete = (period) => {
    openConfirm({
      title: 'Delete enrollment period?',
      description: `${period.name} will be removed. Employees will no longer see this window for enrollment or qualifying events until you create a replacement period.`,
      confirmLabel: 'Delete enrollment period',
      isDestructive: true,
      onConfirm: () => deletePeriod(period)
    });
  };

  const getEmployerName = (employerId) => {
    const employer = employers.find(e => e.id === employerId);
    return employer?.company_name || 'Unknown';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'upcoming': return 'bg-blue-100 text-blue-700';
      case 'closed': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPeriodTypeLabel = (type) => {
    switch (type) {
      case 'open_enrollment': return 'Open Enrollment';
      case 'new_hire': return 'New Hire';
      case 'qualifying_event': return 'Qualifying Event';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Enrollment Periods</h1>
          <p className="text-slate-500 mt-1">
            Manage open enrollment, new hire, and qualifying life event windows
          </p>
        </div>
        <Button 
          onClick={() => openModal()}
          className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Enrollment Period
        </Button>
      </div>

      {isSystemAdmin && (
        <Card>
          <CardContent className="p-4">
            <div className="grid gap-2">
              <Label>Employer</Label>
              <Select value={selectedEmployerId} onValueChange={handleEmployerChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employer" />
                </SelectTrigger>
                <SelectContent>
                  {employers.map((employer) => (
                    <SelectItem key={employer.id} value={employer.id}>
                      {employer.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {periods.map((period, idx) => (
          <motion.div
            key={period.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg text-slate-900">{period.name}</h3>
                      <Badge className={getStatusColor(period.status)}>
                        {period.status}
                      </Badge>
                      <Badge variant="outline">
                        {getPeriodTypeLabel(period.period_type)}
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-slate-600 mb-3">
                      {getEmployerName(period.employer_id)}
                    </p>
                    
                    {period.description && (
                      <p className="text-sm text-slate-600 mb-3">{period.description}</p>
                    )}
                    
                    <div className="flex items-center gap-6 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {format(new Date(period.start_date), 'MMM d, yyyy')} - {format(new Date(period.end_date), 'MMM d, yyyy')}
                        </span>
                      </div>
                      
                      {period.allow_enrollment_outside_window && (
                        <Badge variant="outline" className="text-orange-600 border-orange-600">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Late Enrollment Allowed
                        </Badge>
                      )}
                      
                      {!period.enforce_60_day_wait && (
                        <Badge variant="outline" className="text-blue-600 border-blue-600">
                          No Wait Period
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openModal(period)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => confirmDelete(period)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {periods.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Enrollment Periods</h3>
            <p className="text-slate-500 mb-4">Create your first enrollment period to get started</p>
            <Button onClick={() => openModal()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Enrollment Period
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPeriod ? 'Edit Enrollment Period' : 'Add Enrollment Period'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Employer *</Label>
              <Select value={formData.employer_id} onValueChange={(value) => setFormData({ ...formData, employer_id: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employer..." />
                </SelectTrigger>
                <SelectContent>
                  {employers.map(employer => (
                    <SelectItem key={employer.id} value={employer.id}>
                      {employer.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Period Type *</Label>
              <Select value={formData.period_type} onValueChange={(value) => setFormData({ ...formData, period_type: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open_enrollment">Open Enrollment</SelectItem>
                  <SelectItem value="new_hire">New Hire Window</SelectItem>
                  <SelectItem value="qualifying_event">Qualifying Life Event</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., 2025 Open Enrollment"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional notes about this enrollment period"
                rows={3}
              />
            </div>

            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={formData.allow_enrollment_outside_window}
                  onCheckedChange={(checked) => setFormData({ ...formData, allow_enrollment_outside_window: checked })}
                  id="allow_outside"
                />
                <div>
                  <Label htmlFor="allow_outside" className="cursor-pointer font-medium">
                    Allow late enrollments (admin override)
                  </Label>
                  <p className="text-sm text-slate-600">
                    Permit enrollments outside the standard window
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  checked={formData.enforce_60_day_wait}
                  onCheckedChange={(checked) => setFormData({ ...formData, enforce_60_day_wait: checked })}
                  id="enforce_wait"
                />
                <div>
                  <Label htmlFor="enforce_wait" className="cursor-pointer font-medium">
                    Enforce employer wait period
                  </Label>
                  <p className="text-sm text-slate-600">
                    New hires must wait before benefits become available
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-emerald-500 hover:bg-emerald-600">
              {editingPeriod ? 'Update Period' : 'Create Period'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}